/**
 * hk_os · <ScanRoomButton>
 * ────────────────────────────────────────────────────────────────────
 * Universal QR entry point. Attendants tap it → device camera launches
 * → voice prompt plays automatically. This primitive owns the UI + voice
 * prompt only; the actual decoding is done by the consumer via `onScan`
 * (which receives a raw decoded string) or an external scanner sheet.
 *
 * Props:
 *   voiceKey   — snippet key that plays on launch (default VOICE_SCAN_ROOM)
 *   onLaunch   — called AFTER voice starts, must open the camera
 *   disabled
 *   testid
 */
import React from "react";
import { QrCode } from "lucide-react";
import { useVoiceRegistry } from "./useVoiceRegistry";

export function ScanRoomButton({
  voiceKey = "VOICE_SCAN_ROOM",
  onLaunch,
  disabled = false,
  testid,
}) {
  const { getSnippet, play } = useVoiceRegistry();
  const snippet = getSnippet(voiceKey);

  const handleClick = async () => {
    if (disabled) return;
    // Kick off voice + camera in parallel — voice should never block camera.
    play(voiceKey).catch(() => {});
    onLaunch?.();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      data-testid={testid || "scan-room-btn"}
      className={[
        "group w-full min-h-[112px] rounded-3xl border-2",
        "flex items-center gap-5 p-5 sm:p-6 text-left",
        "transition-all active:scale-[0.98]",
        "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-300",
        disabled
          ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
          : "border-sky-600 bg-sky-50 hover:bg-sky-100 text-sky-900",
      ].join(" ")}
    >
      <span className="shrink-0 rounded-2xl bg-sky-600 text-white p-4 group-active:bg-sky-800">
        <QrCode className="w-10 h-10" strokeWidth={2.4} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-semibold uppercase tracking-widest opacity-70">
          Step 1
        </span>
        <span
          data-testid={testid ? `${testid}-text` : "scan-room-btn-text"}
          className="block text-[24px] sm:text-[28px] font-bold leading-tight truncate"
          lang={snippet?.locale || "bn-IN"}
        >
          {snippet?.text || "Scan Room"}
        </span>
      </span>
    </button>
  );
}

export default ScanRoomButton;
